// src/lib/api.js

// ---------- Base config ----------
// Tolérant: accepte VITE_API_BASE ou VITE_API_URL ; default = /api (proxy)
let RAW_BASE = import.meta.env.VITE_API_BASE || import.meta.env.VITE_API_URL || '/api';
let BASE = RAW_BASE.replace(/\/$/, '');

// Dev convenience: when running the frontend in the browser on localhost and
// no explicit VITE_API_* was provided, point the client at the local backend
// to avoid proxy/config issues (helps during quick dev runs).
try {
  if (typeof window !== 'undefined' && window.location && window.location.hostname === 'localhost') {
    const hasExplicit = Boolean(import.meta.env.VITE_API_BASE || import.meta.env.VITE_API_URL);
    if (!hasExplicit) {
      BASE = 'http://localhost:3000/api';
    }
  }
} catch (e) {
  // ignore in non-browser environments
}

// Public backend origin used to build absolute URLs for uploaded files.
// Prefer explicit VITE_BACKEND_PUBLIC_URL (e.g. http://localhost:3000).
// Otherwise attempt to derive from VITE_API_URL / VITE_API_BASE by stripping trailing /api.
const deriveBackendPublic = () => {
  const explicit = import.meta.env.VITE_BACKEND_PUBLIC_URL;
  if (explicit) return explicit.replace(/\/$/, '');
  const candidate = import.meta.env.VITE_API_URL || import.meta.env.VITE_API_BASE || '';
  if (candidate && candidate.startsWith('http')) return candidate.replace(/\/api\/?$/, '').replace(/\/$/, '');
  // no reliable backend origin known; leave empty so callers can fallback to window.location.origin
  try {
    if (typeof window !== 'undefined' && window.location && window.location.hostname === 'localhost') {
      // dev convenience: prefer local backend at port 3000 when running frontend on localhost
      return 'http://localhost:3000';
    }
  } catch (e) {}
  return '';
};
export const BACKEND_PUBLIC = deriveBackendPublic();

const TDV_USER_KEY = 'tdv_current_user';
const TDV_TOKEN_KEY = 'tdv_auth_token';

const getToken = () => localStorage.getItem(TDV_TOKEN_KEY) || '';
const authHeaders = () => (getToken() ? { Authorization: `Bearer ${getToken()}` } : {});

const qs = (params = {}) => {
  const s = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === null || v === '') return;
    if (Array.isArray(v) && v.length === 0) return;
    s.set(k, Array.isArray(v) ? v.join(',') : String(v));
  });
  const str = s.toString();
  return str ? `?${str}` : '';
};

// helper pour normaliser les listes
const toArray = (res) => {
  if (Array.isArray(res)) return res;
  if (res?.items && Array.isArray(res.items)) return res.items;
  if (res?.rows && Array.isArray(res.rows)) return res.rows;
  if (res?.data && Array.isArray(res.data)) return res.data;
  return []; // fallback
};

import { dbg } from './logger';


export async function http(url, opts = {}) {
  // attach token + sane headers for JSON/FormData
  const token = localStorage.getItem('tdv_auth_token');
  const headers = new Headers(opts.headers || {});

  const isFormData = typeof FormData !== 'undefined' && opts.body instanceof FormData;

  // If body is a plain object (not FormData and not already a string), stringify it
  if (!isFormData && opts.body && typeof opts.body !== 'string') {
    try {
      opts.body = JSON.stringify(opts.body);
    } catch (e) {
      // fallback: leave body as-is
    }
  }

  // If sending JSON and no content-type set, set it
  if (!isFormData && opts.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  // Attach bearer token when available and not already set

  if (token) headers.set('Authorization', `Bearer ${token}`);

  const finalOpts = { ...opts, headers };

  // Debug: log request shape (avoid logging huge bodies in production)
  try {
    dbg('[api] request', { url, method: finalOpts.method || 'GET', body: finalOpts.body ? (typeof finalOpts.body === 'string' ? finalOpts.body : '[non-string body]') : null });
  } catch (e) {
    dbg('[api] request (unable to stringify body)');
  }

  const res = await fetch(BASE + url, finalOpts);

  // Always read response text so we can surface server errors
  const text = await res.text();

  // Try parse JSON, fallback to raw text
  let parsed;
  try {
    parsed = text ? JSON.parse(text) : null;
  } catch (e) {
    parsed = text;
  }

  if (!res.ok) {
    const err = new Error(`HTTP ${res.status} ${res.statusText}: ${typeof parsed === 'string' ? parsed : JSON.stringify(parsed)}`);
    err.status = res.status;
    err.responseBody = parsed;
    try {
      dbg('[api] response error', { url, status: res.status, body: parsed });
    } catch (e) {}
    // On 401, clear client session and redirect to login to force re-authentication
    if (res.status === 401) {
      try {
        logoutUser();
      } catch (e) {}
      try {
        if (typeof window !== 'undefined') {
          const path = (window.location && window.location.pathname) ? window.location.pathname : '';
          const goHome = path === '/wishlists' || path.startsWith('/admin') || path.startsWith('/catalogue');
          window.location.href = goHome ? '/' : '/login';
        }
      } catch (e) {}
    }
    throw err;
  }

  return parsed;
}

// ---------- Session helpers (utilisés par Header/ProtectedRoute/Login) ----------
export function setSession({ user, token }) {
  if (user) localStorage.setItem(TDV_USER_KEY, JSON.stringify(user));
  if (token) localStorage.setItem(TDV_TOKEN_KEY, token);
  window.dispatchEvent(new Event('authChange'));
}
export function getCurrentUser() {
  try {
    const raw = localStorage.getItem(TDV_USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}
export function logoutUser() {
  localStorage.removeItem(TDV_USER_KEY);
  localStorage.removeItem(TDV_TOKEN_KEY);
  window.dispatchEvent(new Event('authChange'));
}

// ---------- Auth ----------
export const login = ({ email, password }) =>
  http(`/auth/login`, {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });

export const getMe = async () => {
  const res = await http(`/auth/me`);
  dbg('[api] getMe response:', res);
  if (res?.user) {
    // merge into existing session but do NOT overwrite existing non-empty fields with undefined/null
    const prev = getCurrentUser() || {};
    const merged = { ...prev };
    for (const [k,v] of Object.entries(res.user)) {
      if (v !== undefined && v !== null && v !== '') merged[k] = v;
      // preserve previous role if backend didn't send it
      if ((k === 'role') && (v === undefined || v === null || v === '')) merged[k] = prev[k];
    }
    setSession({ user: merged });
  }
  return res;
};

export const updateProfile = async (payload) => {
  const res = await http(`/auth/profile`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
  // rafraîchir session si user retourné
  if (res?.user) {
    const prev = getCurrentUser() || {};
    const merged = { ...prev };
    for (const [k,v] of Object.entries(res.user)) {
      if (v !== undefined && v !== null && v !== '') merged[k] = v; else if (!(k in merged)) merged[k] = v; // keep explicit nulls for cleared fields
    }
    // preserve role if not returned
    if (!('role' in res.user) && prev.role) merged.role = prev.role;
    setSession({ user: merged });
  }
  return res;
};

export const requestPasswordReset = (email) =>
  http(`/auth/reset-password`, {
    method: 'POST',
    body: JSON.stringify({ email }),
  });

// Change password (authenticated)
export const changePassword = ({ currentPassword, newPassword }) =>
  http(`/auth/change-password`, {
    method: 'POST',
    body: JSON.stringify({ currentPassword, newPassword }),
  });

// ---------- Catalogue (public) ----------
export const fetchProducts = (params) => http(`/products${qs(params)}`);
export const fetchProductById = (id) => http(`/products/${id}`);
export const fetchCategories = () => http(`/categories`);
export const fetchSubcategories = (categoryId) =>
  http(`/subcategories${qs({ categoryId })}`);
// manufacturers endpoint removed from backend; do not call it from the frontend
export const fetchTags = () => http(`/tags`);
export const fetchSuppliers = () => http(`/suppliers`);
export const fetchBrands = (supplierId) => http(`/brands${qs({ supplierId })}`);
export const fetchCollections = (brandId) => http(`/collections${qs({ brandId })}`);
export const fetchFamilies = () => http(`/families`);

// ---------- Wishlists / Boards (auth) ----------
export const fetchWishlists = async () => {
  const res = await http(`/wishlists`);
  return toArray(res);
};

export const createWishlist = (name) =>
  http(`/wishlists`, { method: 'POST', body: JSON.stringify({ name }) });

export const renameWishlist = (id, name) =>
  http(`/wishlists/${id}`, { method: 'PATCH', body: JSON.stringify({ name }) });

export const updateWishlistStatus = (id, status) =>
  http(`/wishlists/${id}`, { method: 'PATCH', body: JSON.stringify({ status }) });

export const deleteWishlist = (id) =>
  http(`/wishlists/${id}`, { method: 'DELETE' });

export const addProductToWishlist = (wishlistId, payload) =>
  http(`/wishlists/${wishlistId}/products`, {
     method: 'POST',
     body: JSON.stringify(payload),
   });
// ---- Custom products + settings (NOUVEAU) ----
export const addCustomProductToWishlist = (wishlistId, payload) =>
  http(`/wishlists/${wishlistId}/custom-products`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });

export const updateWishlistProduct = (wishlistId, uniqueId, payload) =>
  http(`/wishlists/${wishlistId}/custom-products/${encodeURIComponent(uniqueId)}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });

export const removeProductFromWishlist = (wishlistId, uniqueId) =>
  http(`/wishlists/${wishlistId}/custom-products/${encodeURIComponent(uniqueId)}`, {
    method: 'DELETE',
  });

export const updateWishlistSettings = (wishlistId, settings) =>
  http(`/wishlists/${wishlistId}/settings`, {
    method: 'PATCH',
    body: JSON.stringify(settings),
  });

// ---------- Sourcing email (send with attachments) ----------
export const sendSourcingEmail = (wishlistId, payload) =>
  http(`/wishlists/${wishlistId}/send-sourcing-email`, {
    method: 'POST',
    body: JSON.stringify(payload),
  }); // payload: { html, imageSources, to? }

// ---------- Wishlist messages (threads) ----------
export const fetchWishlistMessages = (wishlistId) =>
  http(`/wishlists/${wishlistId}/messages`);

export const postWishlistMessage = (wishlistId, payload) =>
  http(`/wishlists/${wishlistId}/messages`, { method: 'POST', body: JSON.stringify(payload) });

export const markWishlistMessageRead = (wishlistId, messageId) =>
  http(`/wishlists/${wishlistId}/messages/${messageId}/read`, { method: 'POST' });

export const markWishlistMessagesRead = (wishlistId) =>
  http(`/wishlists/${wishlistId}/messages/mark-read`, { method: 'POST' });

export const fetchWishlistUnreadCount = (wishlistId) =>
  http(`/wishlists/${wishlistId}/messages/unread-count`);

export const uploadFiles = async (fileList) => {
  const fd = new FormData();
  Array.from(fileList || []).forEach((f) => fd.append('files', f));
  const token = localStorage.getItem('tdv_auth_token');
  // Ensure we have a sane base: if BASE is empty, prefer /api
  const targetBase = (typeof BASE === 'string' && BASE.trim().length) ? BASE : '/api';
  const tryUrls = [`${targetBase}/uploads`];
  // If a BACKEND_PUBLIC is set (absolute origin), prefer returning that as well
  if (typeof BACKEND_PUBLIC === 'string' && BACKEND_PUBLIC.trim()) tryUrls.unshift(`${BACKEND_PUBLIC}/uploads`);

  let lastErr = null;
  for (const url of tryUrls) {
    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: fd,
      });
      if (!res.ok) {
        const text = await res.text().catch(() => '');
        lastErr = new Error(`Upload failed: HTTP ${res.status} ${res.statusText}: ${text}`);
        // try next url on 404 (maybe mismatched base)
        if (res.status === 404) continue;
        throw lastErr;
      }
      return await res.json();
    } catch (e) {
      lastErr = e;
      // try next url
      continue;
    }
  }
  // If we reach here, all attempts failed
  throw lastErr || new Error('Upload failed');
};

// ---------- Access requests ----------
export const submitAccessRequest = (payload) =>
  http(`/access-requests`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });

export const updateAccessRequest = async (id, payload) =>
  http(`/access-requests/${id}`, { method: 'PATCH', body: JSON.stringify(payload) });

// ---------- Admin: Access requests ----------
export const fetchAccessRequests = async () => {
  try {
    const res = await http(`/admin/access-requests`);
    return toArray(res);
  } catch (err) {
    if (err?.status === 404) {
      console.warn('[api] fetchAccessRequests returned 404, returning empty array');
      return [];
    }
    throw err;
  }
};
export const approveAccessRequest = (email) =>
  http(`/admin/access-requests/approve`, {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
export const rejectAccessRequest = (email) =>
  http(`/admin/access-requests/reject`, {
    method: 'POST',
    body: JSON.stringify({ email }),
  });

// ---------- Admin: Users ----------
export const fetchUsers = async () => {
  try {
    const res = await http(`/admin/users`);
    return toArray(res);
  } catch (err) {
    if (err?.status === 404) {
      console.warn('[api] fetchUsers returned 404, returning empty array');
      return [];
    }
    throw err;
  }
};
export const createUser = (payload) =>
  http(`/admin/users`, { method: 'POST', body: JSON.stringify(payload) });
export const updateUser = (originalEmail, payload) =>
  http(`/admin/users`, {
    method: 'PATCH',
    body: JSON.stringify({ originalEmail, ...payload }),
  });
export const deleteUser = (id) =>
  http(`/admin/users/${id}`, { method: 'DELETE' });

// ---------- Admin: Catalogue CRUD ----------
export const fetchCatalogue = () => http(`/admin/products`);
export const createProduct = (payload) =>
  http(`/admin/products`, { method: 'POST', body: JSON.stringify(payload) });
export const updateProduct = (id, payload) =>
  http(`/admin/products/${id}`, { method: 'PATCH', body: JSON.stringify(payload) });
export const deleteProduct = (id) =>
  http(`/admin/products/${id}`, { method: 'DELETE' });

// ---------- Admin: Taxonomy ----------
export const fetchTaxonomy = () => http(`/admin/taxonomy`);
export const updateTaxonomy = (payload) =>
  http(`/admin/taxonomy`, { method: 'PUT', body: JSON.stringify(payload) });

// ---------- Admin: Suppliers / Brands / Collections CRUD ----------
export const fetchAdminSuppliers = (params = {}) => http(`/admin/suppliers${qs(params)}`);
export const createAdminSupplier = (payload) => http(`/admin/suppliers`, { method: 'POST', body: JSON.stringify(payload) });
export const updateAdminSupplier = (id, payload) => http(`/admin/suppliers/${id}`, { method: 'PATCH', body: JSON.stringify(payload) });
export const deleteAdminSupplier = (id) => http(`/admin/suppliers/${id}`, { method: 'DELETE' });

export const fetchAdminBrands = (params = {}) => http(`/admin/brands${qs(params)}`);
export const createAdminBrand = (payload) => http(`/admin/brands`, { method: 'POST', body: JSON.stringify(payload) });
export const updateAdminBrand = (id, payload) => http(`/admin/brands/${id}`, { method: 'PATCH', body: JSON.stringify(payload) });
export const deleteAdminBrand = (id) => http(`/admin/brands/${id}`, { method: 'DELETE' });

export const fetchAdminCollections = (params = {}) => http(`/admin/collections${qs(params)}`);
export const createAdminCollection = (payload) => http(`/admin/collections`, { method: 'POST', body: JSON.stringify(payload) });
export const updateAdminCollection = (id, payload) => http(`/admin/collections/${id}`, { method: 'PATCH', body: JSON.stringify(payload) });
export const deleteAdminCollection = (id) => http(`/admin/collections/${id}`, { method: 'DELETE' });

// ---------- Admin: Upload import catalogue ----------
export const importProductsFile = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  const res = await fetch(`${BASE}/admin/import`, {
    method: 'POST',
    headers: {
      ...authHeaders(), // ⚠️ pas de 'Content-Type' (laisse le browser la poser)
    },
    body: formData,
  });
  if (!res.ok) {
    const t = await res.text().catch(() => '');
    throw new Error(`HTTP ${res.status} ${res.statusText}: ${t}`);
  }
  return res.json();
};

// ---------- Orders ----------
export const fetchOrders = () => http(`/orders`);
// debug statements removed to avoid printing backend/role info in the browser console
